import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertOctagon, CheckCircle2, Loader2, RefreshCw, Undo2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";

/**
 * Phase 101ds — Audit & restauration des WASTE créés par le cron auto.
 *
 * Phase 101ds-ux (Feb 2026) — améliorations ergonomie :
 *  - Défaut sur 7 jours (au lieu de 30) — la restauration urgente est
 *    rarement > 1 semaine en arrière.
 *  - Filtre par distributeur (chips) : bascule 1-clic entre les machines.
 *  - Scroll interne (max-h ~500px) : plus besoin de scroller la page.
 *  - Barre d'action sticky en haut de la table (« Tout restaurer » toujours
 *    visible sans avoir à scroller vers le bas).
 */
export default function CronWasteAuditPanel() {
  const [since, setSince] = useState(() => {
    // Défaut : 7 jours en arrière (Phase 101ds-ux — c'était 30j, trop long)
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [items, setItems] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [lastReport, setLastReport] = useState(null);
  const [distFilter, setDistFilter] = useState("__all__");
  const confirm = useConfirm();

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const isoSince = `${since}T00:00:00Z`;
      const { data } = await api.get("/admin/cron-waste-audit", { params: { since: isoSince } });
      setItems(data.items || []);
      setTotalQty(data.total_qty || 0);
      setSelected(new Set((data.items || []).map((g) => g.key)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur audit WASTE");
    } finally {
      setLoading(false);
    }
  }, [since]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  // Phase 101ds-ux — regroupe les distributeurs pour le filtre chips.
  const distributorChips = useMemo(() => {
    const byDist = new Map();
    for (const g of items) {
      const name = g.distributor_name || "?";
      const cur = byDist.get(name) || { name, count: 0, qty: 0 };
      cur.count += 1;
      cur.qty += g.total_qty_wasted || 0;
      byDist.set(name, cur);
    }
    return Array.from(byDist.values()).sort((a, b) => b.qty - a.qty);
  }, [items]);

  const filteredItems = useMemo(() => {
    if (distFilter === "__all__") return items;
    return items.filter((g) => (g.distributor_name || "?") === distFilter);
  }, [items, distFilter]);

  const filteredQty = useMemo(
    () => filteredItems.reduce((acc, g) => acc + (g.total_qty_wasted || 0), 0),
    [filteredItems]
  );

  const toggleKey = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Sélection ne concerne que les items visibles (post-filtre)
  const visibleAllSelected = filteredItems.length > 0 && filteredItems.every((g) => selected.has(g.key));
  const toggleAllVisible = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const g of filteredItems) {
        if (checked) next.add(g.key);
        else next.delete(g.key);
      }
      return next;
    });
  };

  const runRestore = async () => {
    // Ne restaure QUE les groupes visibles + sélectionnés
    const keysToRestore = filteredItems.filter((g) => selected.has(g.key)).map((g) => g.key);
    if (keysToRestore.length === 0) {
      toast.error("Sélectionne au moins un groupe à restaurer");
      return;
    }
    const totalToRestore = filteredItems
      .filter((g) => selected.has(g.key))
      .reduce((acc, g) => acc + g.total_qty_wasted, 0);
    const scopeLabel = distFilter === "__all__" ? "tous distributeurs" : distFilter;
    const ok = await confirm({
      title: "Restaurer les barquettes wastées ?",
      description: `Recréer ${keysToRestore.length} lot(s) totalisant ${totalToRestore} barquette(s) (${scopeLabel}) avec leurs lot_number, DLC, slot et distributeur d'origine.\n\nLes mouvements WASTE historiques restent visibles (traçabilité HACCP). Un mouvement IN de compensation sera créé pour chaque groupe.\n\nCette action est irréversible.`,
      confirmText: `Restaurer ${totalToRestore} barquette(s)`,
      cancelText: "Annuler",
    });
    if (!ok) return;
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/cron-waste-restore", {
        since: `${since}T00:00:00Z`,
        keys: keysToRestore,
      });
      setLastReport(data);
      toast.success(`✅ ${data.restored_count} groupe(s) restauré(s) · ${data.total_qty_restored} barquette(s)`);
      await loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur restauration");
    } finally {
      setRestoring(false);
    }
  };

  const selectedVisibleCount = filteredItems.filter((g) => selected.has(g.key)).length;
  const selectedVisibleQty = filteredItems
    .filter((g) => selected.has(g.key))
    .reduce((acc, g) => acc + (g.total_qty_wasted || 0), 0);

  return (
    <div className="border border-[#E8D3A8] bg-[#FBF4E9] rounded-lg" data-testid="cron-waste-audit-panel">
      <div className="p-4 border-b border-[#E8D3A8]">
        <div className="flex items-center gap-2 mb-1">
          <AlertOctagon className="w-5 h-5 text-[#8C6B2F]" />
          <h3 className="font-semibold text-[#5A3D1A]">Audit &amp; restauration auto-WASTE (Phase 101ds)</h3>
        </div>
        <p className="text-xs text-[#8C6B2F] leading-relaxed">
          L&apos;ancien cron 15min appliquait automatiquement des WASTE « Rupture de contrat prestataire » sur chaque écart AM &gt; MOMA-calc.
          Depuis Phase 101ds cet auto-WASTE est désactivé, mais les anciennes destructions peuvent être restaurées ici avec leurs données d&apos;origine.
        </p>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs mb-1 block">Rechercher depuis</Label>
            <Input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="h-9 w-40"
              data-testid="cron-waste-since"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAudit}
            disabled={loading}
            className="h-9"
            data-testid="cron-waste-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Actualiser
          </Button>
          <div className="flex-1" />
          <div className="text-xs text-[#5A3D1A]">
            <span className="font-semibold">{items.length}</span> groupe(s) ·
            <span className="font-semibold ml-1 text-[#C05C47]">{totalQty}</span> barquette(s) wastée(s)
          </div>
        </div>

        {items.length === 0 && !loading && (
          <div className="bg-[#EAF4EE] border border-[#5A8F76] rounded-md p-3 text-sm text-[#1F4E3D] flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Aucun WASTE cron détecté sur cette période — rien à restaurer.
          </div>
        )}

        {items.length > 0 && (
          <>
            {/* Phase 101ds-ux — Filtre chips par distributeur */}
            {distributorChips.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap" data-testid="cron-waste-dist-chips">
                <span className="text-[10px] uppercase tracking-wider text-[#8C6B2F] mr-1">Distributeur :</span>
                <button
                  onClick={() => setDistFilter("__all__")}
                  className={`px-2 py-1 rounded-full text-[11px] border transition-colors ${
                    distFilter === "__all__"
                      ? "bg-[#8C6B2F] text-white border-[#8C6B2F]"
                      : "bg-white text-[#5A3D1A] border-[#E8D3A8] hover:bg-[#F7EBD3]"
                  }`}
                  data-testid="cron-waste-dist-all"
                >
                  Tous <span className="opacity-70">({items.length})</span>
                </button>
                {distributorChips.map((d) => (
                  <button
                    key={d.name}
                    onClick={() => setDistFilter(d.name)}
                    className={`px-2 py-1 rounded-full text-[11px] border transition-colors ${
                      distFilter === d.name
                        ? "bg-[#8C6B2F] text-white border-[#8C6B2F]"
                        : "bg-white text-[#5A3D1A] border-[#E8D3A8] hover:bg-[#F7EBD3]"
                    }`}
                    data-testid={`cron-waste-dist-${d.name.replace(/\s+/g, "-")}`}
                  >
                    {d.name} <span className="opacity-70">({d.count} · {d.qty}b.)</span>
                  </button>
                ))}
              </div>
            )}

            {/* Phase 101ds-ux — barre d'action sticky en haut de la table */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-[#FBF4E9] py-2 border-b border-[#E8D3A8]">
              <Badge variant="outline" className="bg-white">
                {selectedVisibleCount} / {filteredItems.length} sélectionné(s)
                {distFilter !== "__all__" && (
                  <span className="ml-1 text-[#8C6B2F]">· {selectedVisibleQty} barquette(s)</span>
                )}
                {distFilter === "__all__" && filteredQty !== totalQty && (
                  <span className="ml-1 text-[#8C6B2F]">· {selectedVisibleQty} barquette(s)</span>
                )}
              </Badge>
              <Button
                onClick={runRestore}
                disabled={restoring || selectedVisibleCount === 0}
                className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white h-8"
                data-testid="cron-waste-restore-btn"
              >
                {restoring ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Undo2 className="w-4 h-4 mr-1" />}
                {distFilter === "__all__"
                  ? `Restaurer ${selectedVisibleCount} groupe(s)`
                  : `Restaurer ${distFilter}`}
              </Button>
            </div>

            {/* Phase 101ds-ux — scroll interne (max-h 500px) */}
            <div className="border border-[#E5E5E0] rounded-md bg-white overflow-auto max-h-[500px]" data-testid="cron-waste-scroll">
              <table className="w-full text-xs">
                <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0] sticky top-0 z-[5]">
                  <tr>
                    <th className="text-left px-2 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={visibleAllSelected}
                        onChange={(e) => toggleAllVisible(e.target.checked)}
                        data-testid="cron-waste-select-all"
                      />
                    </th>
                    <th className="text-left px-2 py-2">Lot</th>
                    <th className="text-left px-2 py-2">Produit</th>
                    <th className="text-left px-2 py-2">Distributeur · Slot</th>
                    <th className="text-center px-2 py-2 w-16">Qté</th>
                    <th className="text-left px-2 py-2">DLC</th>
                    <th className="text-left px-2 py-2">Derniers WASTE</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center px-2 py-4 text-[#8C6B2F] italic">
                        Aucun groupe pour ce filtre — bascule sur « Tous » ou change de distributeur.
                      </td>
                    </tr>
                  )}
                  {filteredItems.map((g) => (
                    <tr
                      key={g.key}
                      className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]"
                      data-testid={`cron-waste-row-${g.lot_number}`}
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(g.key)}
                          onChange={() => toggleKey(g.key)}
                          data-testid={`cron-waste-check-${g.lot_number}`}
                        />
                      </td>
                      <td className="px-2 py-2 font-mono">{g.lot_number || "—"}</td>
                      <td className="px-2 py-2">
                        <div>{g.product_name}</div>
                        <div className="text-[10px] text-[#6B6E6B] font-mono">{g.product_sku}</div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-medium">{g.distributor_name}</div>
                        <div className="text-[10px] text-[#6B6E6B]">slot {g.slot_number}</div>
                      </td>
                      <td className="px-2 py-2 text-center font-mono font-bold text-[#C05C47]">
                        {g.total_qty_wasted}
                      </td>
                      <td className="px-2 py-2 font-mono text-[11px]">
                        {g.dlc || "—"}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-[#6B6E6B]">
                        {g.waste_movements.length} mouvement(s)
                        <div>du {new Date(g.first_waste_at).toLocaleDateString("fr-CH")} au {new Date(g.last_waste_at).toLocaleDateString("fr-CH")}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {lastReport && (
          <div className="mt-4 border border-[#5A8F76] bg-[#EAF4EE] rounded-md p-3 text-xs" data-testid="cron-waste-report">
            <div className="font-semibold text-[#1F4E3D] mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Rapport de restauration
            </div>
            <div className="grid grid-cols-2 gap-2 text-[#1F4E3D]">
              <div>
                <span className="text-[10px] uppercase text-[#6B6E6B]">Groupes restaurés</span>
                <div className="font-mono text-lg">{lastReport.restored_count}</div>
              </div>
              <div>
                <span className="text-[10px] uppercase text-[#6B6E6B]">Barquettes totales</span>
                <div className="font-mono text-lg">{lastReport.total_qty_restored}</div>
              </div>
            </div>
            {lastReport.warnings && lastReport.warnings.length > 0 && (
              <div className="mt-2 border-t border-[#5A8F76] pt-2 text-[#C05C47]">
                <div className="font-semibold">Avertissements :</div>
                {lastReport.warnings.map((w, idx) => (
                  <div key={idx} className="text-[11px]">• {w}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
